import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { AbacPosture, EnforcementState } from '@/shared/api/generated/policy';
import { personName } from '@/shared/model/person';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';

import { useSetAbacEnabled } from '../api/policy';
import { enforcementSummary } from '../model/bundles';
import type { EnforcementReading } from '../model/bundles';

/* The answer to "what decides access here", before anything else on the page.
 *
 * Three parts, each answering one question: the state (is policy narrowing access, and if not, why
 * not), the switch (roles alone, or roles and policy), and the flow (where in a request's path the
 * policy sits, and what it is doing there right now). The flow is the part a reader most needs and a
 * settings page never shows: that a role is always checked first, and that policy can only narrow.
 */

const CHIP = {
  'no-engine': 'policy.hero.chip.noEngine',
  'abac-off': 'policy.hero.chip.off',
  'on-without-policy': 'policy.hero.chip.empty',
  'failing-closed': 'policy.hero.chip.failing',
  enforcing: 'policy.hero.chip.enforcing',
} as const satisfies Record<EnforcementReading, string>;

/** The attribute-policy step of the flow, per reading. Dashed where the step decides nothing. */
const POLICY_NODE = {
  'no-engine': { key: 'policy.flow.policy.noEngine', tone: 'muted', dashed: true },
  'abac-off': { key: 'policy.flow.policy.off', tone: 'muted', dashed: true },
  'on-without-policy': { key: 'policy.flow.policy.empty', tone: 'warn', dashed: true },
  'failing-closed': { key: 'policy.flow.policy.failing', tone: 'error', dashed: false },
  enforcing: { key: 'policy.flow.policy.enforcing', tone: 'accent', dashed: false },
} as const satisfies Record<
  EnforcementReading,
  { readonly key: string; readonly tone: string; readonly dashed: boolean }
>;

export interface PolicyHeroProps {
  readonly enforcement: EnforcementState;
  readonly posture: AbacPosture | undefined;
  readonly postureFailed: boolean;
  readonly governingName: string;
  /** The governing bundle exists. */
  readonly hasGoverning: boolean;
  /** The governing bundle's active version, when it has one. */
  readonly activeSemver: string | null;
  /** The active version's bytes, to tell whether the engine has loaded them yet. */
  readonly activeHash: string | null;
  readonly maxStalenessSeconds: number;
  readonly canConfigure: boolean;
  readonly onOpenVersions: () => void;
  readonly onFlash: (message: string) => void;
}

type Asking = 'off' | 'on' | null;

export function PolicyHero({
  enforcement,
  posture,
  postureFailed,
  governingName,
  hasGoverning,
  activeSemver,
  activeHash,
  maxStalenessSeconds,
  canConfigure,
  onOpenVersions,
  onFlash,
}: PolicyHeroProps): ReactElement {
  const { t } = useTranslation();
  const setEnabled = useSetAbacEnabled();
  const [asking, setAsking] = useState<Asking>(null);

  const summary = enforcementSummary(enforcement);
  const reading = summary.reading;
  const loadedSemver = enforcement.semver ?? activeSemver ?? '';
  const values = { bundle: governingName, semver: loadedSemver };

  /* **Loading** is the active version not yet being the bytes the engine runs: right after a
   * publish or a rollback, the previous version keeps deciding until the registry refreshes. */
  const loading =
    reading === 'enforcing' &&
    activeHash !== null &&
    (enforcement.content_hash ?? null) !== activeHash;

  const enabled = posture?.enabled;
  const node = POLICY_NODE[reading];
  const steps = [
    {
      title: t('policy.flow.request'),
      sub: t('policy.flow.requestSub'),
      tone: 'plain',
      dashed: false,
    },
    {
      title: t('policy.flow.role'),
      sub: t('policy.flow.roleSub'),
      tone: 'plain',
      dashed: false,
    },
    {
      title: t('policy.flow.policy'),
      sub: t(node.key, values),
      tone: node.tone,
      dashed: node.dashed,
    },
    {
      title: t('policy.flow.decision'),
      sub:
        reading === 'failing-closed'
          ? t('policy.flow.refused')
          : t('policy.flow.decides'),
      tone: reading === 'failing-closed' ? 'error' : 'plain',
      dashed: false,
    },
  ];

  const origin =
    posture === undefined
      ? postureFailed
        ? t('policy.mode.unread')
        : ''
      : posture.declared && posture.updated_by
        ? t('policy.mode.setBy', { person: personName(posture.updated_by) })
        : posture.deployment_default
          ? t('policy.mode.defaultOn')
          : t('policy.mode.defaultOff');

  const switchDisabled = !canConfigure || posture === undefined || setEnabled.isPending;
  const onVariant = !enforcement.runtime_available
    ? 'engine'
    : !hasGoverning
      ? 'nothing'
      : activeSemver === null
        ? 'refuse'
        : 'active';

  const confirm = (): void => {
    const turningOn = asking === 'on';
    setEnabled.mutate(turningOn, {
      onSuccess: () => {
        setAsking(null);
        onFlash(turningOn ? t('policy.mode.doneOn') : t('policy.mode.doneOff'));
      },
    });
  };

  return (
    <article className="lq-pq__hero" aria-labelledby="pq-hero-title">
      <div className="lq-pq__heroTop">
        <div className="lq-pq__heroText">
          <span className="lq-pq__chip" data-tone={summary.tone}>
            <span className="lq-pq__chipDot" aria-hidden="true" />
            {t(CHIP[reading])}
          </span>
          <h2 id="pq-hero-title" className="lq-pq__heroTitle">
            {t(summary.titleKey, values)}
          </h2>
          <p className="lq-pq__heroBody">{t(summary.bodyKey, values)}</p>
          {reading === 'failing-closed' ? (
            <button
              type="button"
              className="lq-pq__btn lq-pq__btn--alarm"
              onClick={onOpenVersions}
            >
              {t('policy.hero.openVersions')}
            </button>
          ) : null}
          {loading ? (
            <p className="lq-pq__loadingLine" role="status">
              <span className="lq-pq__spin" aria-hidden="true" />
              {t('policy.hero.loading', {
                semver: activeSemver ?? '',
                seconds: maxStalenessSeconds,
              })}
            </p>
          ) : null}
        </div>

        <div className="lq-pq__modeBox">
          <div
            className="lq-pq__seg"
            role="radiogroup"
            aria-label={t('policy.mode.group')}
            title={canConfigure ? undefined : t('policy.mode.noPermission')}
          >
            <button
              type="button"
              role="radio"
              className="lq-pq__segOption"
              aria-checked={enabled === false}
              disabled={switchDisabled}
              onClick={() => {
                if (enabled !== false) setAsking('off');
              }}
            >
              {t('policy.mode.rolesOnly')}
            </button>
            <button
              type="button"
              role="radio"
              className="lq-pq__segOption"
              aria-checked={enabled === true}
              disabled={switchDisabled}
              onClick={() => {
                if (enabled !== true) setAsking('on');
              }}
            >
              {t('policy.mode.rolesAndPolicy')}
            </button>
          </div>
          <span className="lq-pq__modeBy">{origin}</span>
        </div>
      </div>

      <ol className="lq-pq__flow" aria-label={t('policy.flow.label')}>
        {steps.map((step, index) => (
          <li key={step.title} className="lq-pq__flowStep">
            <div
              className="lq-pq__node"
              data-tone={step.tone}
              data-dashed={step.dashed ? 'true' : undefined}
            >
              <span className="lq-pq__nodeTitle">{step.title}</span>
              <span className="lq-pq__nodeSub">{step.sub}</span>
            </div>
            {index < steps.length - 1 ? (
              <svg
                className="lq-pq__arrow"
                width="28"
                height="12"
                viewBox="0 0 28 12"
                aria-hidden="true"
              >
                <path d="M3 6h20M18 2l5 4-5 4" />
              </svg>
            ) : null}
          </li>
        ))}
      </ol>

      <ConfirmDialog
        open={asking !== null}
        title={
          asking === 'off'
            ? t('policy.mode.confirmOff.title')
            : t(`policy.mode.confirmOn.${onVariant}.title`, values)
        }
        message={
          asking === 'off'
            ? t('policy.mode.confirmOff.body', { bundle: governingName })
            : t(`policy.mode.confirmOn.${onVariant}.body`, {
                bundle: governingName,
                semver: activeSemver ?? '',
              })
        }
        confirmLabel={
          asking === 'off'
            ? t('policy.mode.confirmOff.do')
            : t(`policy.mode.confirmOn.${onVariant}.do`)
        }
        cancelLabel={t('common.cancel')}
        tone={asking === 'on' && onVariant === 'refuse' ? 'danger' : 'primary'}
        busy={setEnabled.isPending}
        error={setEnabled.isError ? setEnabled.error.message : undefined}
        onConfirm={confirm}
        onCancel={() => {
          setAsking(null);
          setEnabled.reset();
        }}
      />
    </article>
  );
}
